<template>
  <div class="grid grid-cols-3 gap-5 mb-6 w-full">
    <CardWidget
      :title="t('All Patients')"
      :selected="props.selectedWidget === WidgetType.ALL_PATIENTS"
      data-testid="all-patients-widget"
      @click="emit('selected-widget-update', WidgetType.ALL_PATIENTS)"
    >
      <template #content>
        <p data-testid="number-of-patients" class="font-semibold text-3xl">
          {{ numberOfAllPatients }}
        </p>
      </template>
    </CardWidget>

    <CardWidget
      :title="t('AKI Risk Patients')"
      :title-tooltip-text="t('Acute Kidney Injury (AKI) Risk patients')"
      :selected="props.selectedWidget === WidgetType.AKI_PATIENTS"
      :ai-support="true"
      data-testid="aki-risk-widget"
      @click="handleAkiWidgetClick"
    >
      <template #content>
        <div v-if="props.isAkiLoading" class="flex items-center gap-4 min-h-[36px]">
          <VProgressCircular indeterminate color="primary" size="24" width="3" />
          <span class="text-gray-500 text-sm font-medium">{{
            t('Loading AKI predictions...')
          }}</span>
        </div>

        <div
          v-else-if="!hasAcceptedAkiLimitation"
          data-testid="aki-widget-blocked"
          class="flex items-center gap-2 min-h-[36px]"
        >
          <VIcon
            icon="visibility_off"
            class="material-icons-outlined"
            color="fg-disabled"
            size="20"
          />
          <span class="text-gray-600 text-sm font-medium">
            {{
              t(
                'This sections highlights patients with an increased risk of acute kidney injury (AKI).'
              )
            }}
          </span>
        </div>

        <div v-else-if="numberOfAkiPatients == null" class="flex items-center gap-2 min-h-[36px]">
          <VIcon icon="error_outline" color="error" size="20" />
          <span class="text-red-500 text-sm font-medium">{{ t('Data unavailable') }}</span>
        </div>

        <div v-else class="flex justify-between">
          <VTooltip
            :text="
              t('{x} patients with high or very high risk for AKI', { x: numberOfAkiPatients })
            "
            :location="'bottom start'"
            :offset="0"
            :open-delay="500"
          >
            <template #activator="{ props: activatorProps }">
              <p
                v-bind="activatorProps"
                data-testid="number-of-aki-patients"
                class="font-semibold text-3xl"
              >
                {{ numberOfAkiPatients }}
              </p>
            </template>
          </VTooltip>

          <div class="flex gap-4">
            <div class="grid grid-cols-[repeat(2,max-content)] grid-rows-2 gap-x-2 items-center">
              <div class="dot bg-[#E71010]" />
              <p class="text-gray-600 font-semibold text-[12px]">{{ t('Very High Risk') }}</p>
              <div />
              <p class="font-semibold">{{ props.veryHighRiskAkiPatients }} {{ t('in total') }}</p>
            </div>

            <div class="grid grid-cols-[repeat(2,max-content)] grid-rows-2 gap-x-2 items-center">
              <div class="dot bg-[#FF9900]" />
              <p class="text-gray-600 font-semibold text-[12px]">{{ t('High Risk') }}</p>
              <div />
              <p class="font-semibold">{{ props.highRiskAkiPatients }} {{ t('in total') }}</p>
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <VChip
          :style="{ '--v-border-opacity': '1', '--v-activated-opacity': '0' }"
          :text="t('AI-Generated Output')"
          :density="'compact'"
          :size="20"
          class="bg-utility-blue-50 text-utility-blue-700 border-utility-blue-200 border-sm font-semibold text-xs tracking-normal px-2 w-fit"
        />
      </template>
    </CardWidget>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import CardWidget from '@/components/patient/components/molecules/CardWidget.vue'
import { WidgetType } from '@/enums/WidgetType'
import { useAkiPlugin } from '@/composables/useAkiPlugin'

interface Props {
  selectedWidget: WidgetType
  totalCases: number
  filteredCases: number
  highRiskAkiPatients?: number | null
  veryHighRiskAkiPatients?: number | null
  isAkiLoading?: boolean
}

interface Emits {
  (e: 'selected-widget-update', type: WidgetType): void
}

const { t } = useI18n()
const { hasAcceptedAkiLimitation } = useAkiPlugin()

const props = withDefaults(defineProps<Props>(), {
  highRiskAkiPatients: null,
  veryHighRiskAkiPatients: null,
  isAkiLoading: false
})

const emit = defineEmits<Emits>()

const numberOfAllPatients = computed<string>(() => {
  if (props.totalCases === props.filteredCases) {
    return props.totalCases.toString()
  } else {
    return `${props.filteredCases} ${t('of')} ${props.totalCases}`
  }
})

const numberOfAkiPatients = computed(() => {
  if (props.veryHighRiskAkiPatients == null || props.highRiskAkiPatients == null) {
    return null
  }
  return props.veryHighRiskAkiPatients + props.highRiskAkiPatients
})

function handleAkiWidgetClick() {
  // When the AKI contraindications have not been accepted yet, the counts are
  // intentionally not loaded - the click must still open the AKI view so the
  // consent form is reachable.
  if (props.isAkiLoading || (hasAcceptedAkiLimitation.value && numberOfAkiPatients.value == null)) {
    return
  }
  emit('selected-widget-update', WidgetType.AKI_PATIENTS)
}
</script>

<style scoped lang="scss">
.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
</style>
